/**
 * RestorePurchasesModal.jsx
 *
 * "Restore purchases" flow for guest buyers who:
 *   - Switched devices and lost their localStorage tokens
 *   - Cleared their browser cache
 *   - Bought from a friend's phone
 *
 * TWO modes, tabbed:
 *
 *   1. INSTANT LOOKUP (recommended, works without SMTP):
 *      User enters email + Stripe payment reference (from their Stripe
 *      email receipt — `pi_...` or `cs_...`). Backend validates the
 *      pair and returns the purchase list INLINE on the page. Zero
 *      email dependency.
 *
 *   2. EMAIL A LINK (legacy, requires SMTP working):
 *      User enters just email. Backend emails a magic link with all
 *      their purchase access URLs. Anti-enumeration: always returns
 *      "if we have records under this email, we'll email you."
 *
 * Mode 1 is the default because it's more reliable (Stripe deliverability
 * is best-in-class; our SMTP setup may be less so).
 */
import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Envelope, X, CheckCircle, MagnifyingGlass, ArrowRight, Play } from '@phosphor-icons/react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import api from '@/lib/api';
import { toast } from 'sonner';
import { addGuestPurchase } from '@/lib/guestPurchases';

export default function RestorePurchasesModal({ open, onClose }) {
  const navigate = useNavigate();
  const [mode, setMode] = useState('lookup'); // 'lookup' | 'email'
  const [email, setEmail] = useState('');
  const [stripeRef, setStripeRef] = useState('');
  const [sending, setSending] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [lookupResults, setLookupResults] = useState(null);

  const handleLookup = async (e) => {
    e.preventDefault();
    if (!email || !email.includes('@')) return toast.error('Please enter a valid email');
    if (!stripeRef || stripeRef.length < 8) return toast.error('Please paste your Stripe payment reference');
    setSending(true);
    try {
      const res = await api.post('/audio-tours/access/lookup', {
        email: email.trim(),
        stripe_reference: stripeRef.trim(),
      });
      const purchases = res.data.purchases || [];
      // Stash each token client-side so future visits show "Purchased" automatically
      purchases.forEach((p) => addGuestPurchase(p.tour_id, p.purchase_token));
      setLookupResults(purchases);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not find matching purchase');
    } finally {
      setSending(false);
    }
  };

  const handleEmailSend = async (e) => {
    e.preventDefault();
    if (!email || !email.includes('@')) return toast.error('Please enter a valid email');
    setSending(true);
    try {
      await api.post('/audio-tours/access/restore', { email: email.trim() });
      setEmailSent(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not send restore email');
    } finally {
      setSending(false);
    }
  };

  const handleClose = () => {
    onClose();
    setTimeout(() => {
      setEmailSent(false); setLookupResults(null);
      setEmail(''); setStripeRef(''); setMode('lookup');
    }, 300);
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 z-[200] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
          data-testid="restore-purchases-modal"
          onClick={handleClose}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.96, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.96, y: 20 }}
            className="bg-white rounded-2xl shadow-2xl max-w-lg w-full overflow-hidden max-h-[90vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="relative bg-gradient-to-br from-wine-800 to-wine-900 px-6 py-5 text-white flex-shrink-0">
              <button
                onClick={handleClose}
                className="absolute top-3 right-3 text-white/80 hover:text-white"
                aria-label="Close"
                data-testid="restore-close"
              >
                <X size={20} />
              </button>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-white/15 flex items-center justify-center">
                  <MagnifyingGlass size={22} />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-wider text-white/70">Restore your purchases</div>
                  <div className="font-serif text-lg">Access on any device</div>
                </div>
              </div>
            </div>

            <div className="px-6 py-5 overflow-y-auto">
              {/* Lookup results view */}
              {lookupResults ? (
                <div className="space-y-4" data-testid="restore-lookup-results">
                  <div className="rounded-lg bg-emerald-50 border border-emerald-200 px-3 py-2 text-sm text-emerald-900 flex items-start gap-2">
                    <CheckCircle size={18} weight="fill" className="text-emerald-600 mt-0.5 flex-shrink-0" />
                    <div>
                      Found {lookupResults.length} purchase{lookupResults.length === 1 ? '' : 's'} — saved on this device.
                      Bookmark any access link to keep it for later.
                    </div>
                  </div>
                  {lookupResults.map((p) => (
                    <div key={p.purchase_token} className="flex gap-3 border rounded-xl p-3 hover:bg-gray-50 transition">
                      {p.cover_image_url && (
                        <img src={p.cover_image_url} alt="" className="w-16 h-16 rounded-lg object-cover flex-shrink-0" />
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-sm text-gray-900 truncate">{p.title}</div>
                        <div className="text-xs text-gray-500 truncate">{p.winery_name}</div>
                        {p.duration_minutes && (
                          <div className="text-xs text-gray-400">{p.duration_minutes} min · purchased {p.purchase_date}</div>
                        )}
                      </div>
                      <Button
                        size="sm"
                        className="bg-wine-700 hover:bg-wine-800 text-white self-center"
                        onClick={() => { handleClose(); navigate(`/audio-tours/access/${p.purchase_token}`); }}
                        data-testid={`restore-result-play-${p.tour_id}`}
                      >
                        <Play size={14} weight="fill" className="mr-1" /> Play
                      </Button>
                    </div>
                  ))}
                  <Button onClick={handleClose} variant="outline" className="w-full">Done</Button>
                </div>
              ) : emailSent ? (
                <div className="text-center py-6 space-y-4" data-testid="restore-sent-confirmation">
                  <div className="w-14 h-14 rounded-full bg-emerald-100 flex items-center justify-center mx-auto">
                    <CheckCircle size={36} weight="fill" className="text-emerald-600" />
                  </div>
                  <div>
                    <div className="font-semibold text-gray-900 mb-1">Check your inbox</div>
                    <p className="text-sm text-gray-600">
                      If we have any audio tours under <strong>{email}</strong>, we just sent you a link. Works on any device.
                    </p>
                  </div>
                  <Button onClick={handleClose} variant="outline" className="w-full">Got it</Button>
                </div>
              ) : (
                <>
                  {/* Mode tabs */}
                  <div className="flex gap-1 rounded-lg bg-gray-100 p-1 mb-5" data-testid="restore-mode-tabs">
                    <button
                      onClick={() => setMode('lookup')}
                      className={`flex-1 text-xs font-semibold px-3 py-2 rounded-md transition ${
                        mode === 'lookup' ? 'bg-white shadow text-wine-800' : 'text-gray-500 hover:text-gray-700'
                      }`}
                      data-testid="restore-mode-lookup"
                    >
                      Instant lookup
                    </button>
                    <button
                      onClick={() => setMode('email')}
                      className={`flex-1 text-xs font-semibold px-3 py-2 rounded-md transition ${
                        mode === 'email' ? 'bg-white shadow text-wine-800' : 'text-gray-500 hover:text-gray-700'
                      }`}
                      data-testid="restore-mode-email"
                    >
                      Email me a link
                    </button>
                  </div>

                  {mode === 'lookup' ? (
                    <form onSubmit={handleLookup} className="space-y-4" data-testid="restore-lookup-form">
                      <p className="text-sm text-gray-700">
                        Verify your purchase in seconds using your Stripe receipt. Look in the receipt email
                        from <span className="font-mono text-xs">receipts@stripe.com</span> for the Payment ID
                        (starts with <span className="font-mono text-xs">pi_</span>).
                      </p>
                      <div>
                        <label className="text-xs font-semibold text-gray-700 uppercase tracking-wide block mb-1.5">
                          Email used at checkout
                        </label>
                        <input
                          type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                          placeholder="you@example.com"
                          className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-wine-600/40 focus:border-wine-600"
                          data-testid="restore-lookup-email" disabled={sending} required autoFocus
                        />
                      </div>
                      <div>
                        <label className="text-xs font-semibold text-gray-700 uppercase tracking-wide block mb-1.5">
                          Stripe payment ID
                        </label>
                        <input
                          type="text" value={stripeRef} onChange={(e) => setStripeRef(e.target.value)}
                          placeholder="pi_3AbCdEfGh1234567890..."
                          className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-wine-600/40 focus:border-wine-600"
                          data-testid="restore-lookup-ref" disabled={sending} required
                        />
                        <p className="text-xs text-gray-500 mt-1">From your Stripe email receipt (Payment ID, Reference #, or Session ID).</p>
                      </div>
                      <Button
                        type="submit" disabled={sending}
                        className="w-full bg-wine-700 hover:bg-wine-800 text-white"
                        data-testid="restore-lookup-submit"
                      >
                        {sending ? 'Looking up…' : (
                          <><MagnifyingGlass size={16} className="mr-2" /> Find my purchases <ArrowRight size={14} className="ml-1" /></>
                        )}
                      </Button>
                      <p className="text-xs text-gray-500 text-center">
                        Have an account? <a href="/signin" className="text-wine-700 hover:underline">Sign in</a> — your purchases auto-sync.
                      </p>
                    </form>
                  ) : (
                    <form onSubmit={handleEmailSend} className="space-y-4" data-testid="restore-email-form">
                      <p className="text-sm text-gray-700">
                        Enter the email you used at checkout. We&apos;ll email you a link to every audio tour you own.
                      </p>
                      <div>
                        <label className="text-xs font-semibold text-gray-700 uppercase tracking-wide block mb-1.5">Email</label>
                        <input
                          type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                          placeholder="you@example.com"
                          className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-wine-600/40 focus:border-wine-600"
                          data-testid="restore-email-input" disabled={sending} required autoFocus
                        />
                      </div>
                      <Button
                        type="submit" disabled={sending}
                        className="w-full bg-wine-700 hover:bg-wine-800 text-white"
                        data-testid="restore-submit"
                      >
                        {sending ? 'Sending…' : (
                          <><Envelope size={16} className="mr-2" /> Send my access link</>
                        )}
                      </Button>
                      <p className="text-xs text-gray-500 text-center">
                        No email arriving? Try <button type="button" className="text-wine-700 hover:underline" onClick={() => setMode('lookup')}>instant lookup</button> instead.
                      </p>
                    </form>
                  )}
                </>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
